import { useCallback, useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import {
  Upload, Plus, Trash2, Download, Users, User, CheckCircle,
  XCircle, AlertTriangle, FileSpreadsheet, ChevronRight, RefreshCw,
  AlertCircle,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { Header } from "./Schools";

// ── Shared primitives ─────────────────────────────────────────────────────────
const SI = "w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 text-sm bg-white";

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="text-sm font-medium text-gray-700 block mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

// Status badge for preview table rows
const RowBadge = ({ status }) => {
  const cfg = {
    valid:     { label: "Valid",     cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    invalid:   { label: "Invalid",   cls: "bg-red-50 text-red-700 border-red-200" },
    duplicate: { label: "Duplicate", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  }[status] || { label: status, cls: "bg-gray-100 text-gray-600 border-gray-200" };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

// ── Individual registration form ───────────────────────────────────────────────
function IndividualForm({ schoolId, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", subject: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email format.";
    if (form.phone && !/^[\d\s\+\-\(\)]{7,15}$/.test(form.phone)) e.phone = "Invalid phone number.";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!schoolId) { toast.error("Please select a school first."); return; }
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await api.post("/teachers", { ...form, name: form.name.trim(), school_id: schoolId });
      toast.success(`${form.name} added successfully.`);
      setForm({ name: "", email: "", subject: "", phone: "" });
      setErrors({});
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add teacher.");
    }
    setSaving(false);
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6">
      <div className="flex items-center gap-2 mb-5">
        <User className="w-4 h-4 text-[#4F46E5]" />
        <h3 className="font-semibold text-[#0F172A]">Teacher Details</h3>
      </div>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name" required error={errors.name}>
          <input value={form.name} onChange={set("name")} placeholder="e.g. Jane Smith" className={SI} data-testid="teacher-name" />
        </Field>
        <Field label="Email address" error={errors.email}>
          <input type="email" value={form.email} onChange={set("email")} placeholder="jane@school.edu" className={SI} data-testid="teacher-email" />
        </Field>
        <Field label="Subject(s)">
          <input value={form.subject} onChange={set("subject")} placeholder="e.g. Mathematics, Science" className={SI} data-testid="teacher-subject" />
        </Field>
        <Field label="Phone number" error={errors.phone}>
          <input type="tel" value={form.phone} onChange={set("phone")} placeholder="e.g. 9876543210" className={SI} data-testid="teacher-phone" />
        </Field>
        <div className="sm:col-span-2 flex justify-end pt-2">
          <button type="submit" disabled={saving}
            className="inline-flex items-center gap-2 bg-[#0F172A] text-white text-sm font-medium px-6 py-2.5 rounded-xl hover:bg-[#1e293b] disabled:opacity-50">
            <Plus className="w-4 h-4" />
            {saving ? "Saving…" : "Add Teacher"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Bulk registration ─────────────────────────────────────────────────────────
function BulkForm({ schoolId, onSaved }) {
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // { rows, summary }
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const reset = () => { setFile(null); setPreview(null); setResult(null); if (fileRef.current) fileRef.current.value = ""; };

  const handleFile = async (f) => {
    if (!f) return;
    if (!schoolId) { toast.error("Please select a school first."); return; }
    if (!/(\.csv|\.xlsx|\.xls)$/i.test(f.name)) { toast.error("Only CSV and XLSX files are supported."); return; }
    setFile(f);
    setPreview(null);
    setResult(null);
    setValidating(true);
    try {
      const fd = new FormData();
      fd.append("school_id", schoolId);
      fd.append("file", f);
      const { data } = await api.post("/teachers/validate", fd);
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not parse file.");
      reset();
    }
    setValidating(false);
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); };
  const onFileChange = (e) => handleFile(e.target.files?.[0]);

  const doImport = async () => {
    if (!file || !schoolId) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("school_id", schoolId);
      fd.append("file", file);
      const { data } = await api.post("/teachers/import", fd);
      setResult(data);
      setPreview(null);
      if (data.inserted > 0) {
        toast.success(`${data.inserted} teacher${data.inserted !== 1 ? "s" : ""} imported successfully.`);
        onSaved();
      } else {
        toast.warning("No new teachers were imported.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed.");
    }
    setImporting(false);
  };

  const downloadTemplate = () => {
    window.open(`${BACKEND}/api/teachers/sample-template`, "_blank");
  };

  const validCount     = preview?.summary?.valid      ?? 0;
  const invalidCount   = preview?.summary?.invalid    ?? 0;
  const duplicateCount = preview?.summary?.duplicates ?? 0;
  const totalCount     = preview?.summary?.total      ?? 0;

  return (
    <div className="grid gap-4">
      {/* Template download */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 flex items-center justify-between gap-4">
        <div>
          <p className="font-medium text-[#0F172A] text-sm">Sample CSV Template</p>
          <p className="text-xs text-gray-500 mt-0.5">Download the template with correct column headers before uploading.</p>
        </div>
        <button onClick={downloadTemplate}
          className="inline-flex items-center gap-2 text-sm font-medium bg-[#EEF2FF] text-[#4F46E5] px-4 py-2.5 rounded-xl hover:bg-[#E0E7FF] shrink-0">
          <Download className="w-4 h-4" /> Download Template
        </button>
      </div>

      {/* Upload zone */}
      {!preview && !result && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`rounded-2xl border-2 border-dashed cursor-pointer transition-all p-10 flex flex-col items-center justify-center gap-3 ${dragOver ? "border-[#4F46E5] bg-[#EEF2FF]" : "border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100"}`}
          data-testid="bulk-drop-zone"
        >
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFileChange} className="hidden" data-testid="bulk-file-input" />
          {validating ? (
            <>
              <RefreshCw className="w-8 h-8 text-[#4F46E5] animate-spin" />
              <p className="text-sm font-medium text-gray-600">Validating file…</p>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-white border border-gray-200 grid place-items-center shadow-sm">
                <FileSpreadsheet className="w-6 h-6 text-[#4F46E5]" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-[#0F172A]">Drop your CSV or XLSX file here</p>
                <p className="text-xs text-gray-400 mt-1">or click to browse — max 5MB</p>
              </div>
              <p className="text-xs text-gray-400">Supported formats: .csv, .xlsx, .xls</p>
            </>
          )}
        </div>
      )}

      {/* Preview table */}
      {preview && (
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          {/* Summary bar */}
          <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm font-semibold text-[#0F172A]">{totalCount} rows found</span>
              <span className="flex items-center gap-1.5 text-sm text-emerald-700"><CheckCircle className="w-3.5 h-3.5" /> {validCount} valid</span>
              {duplicateCount > 0 && <span className="flex items-center gap-1.5 text-sm text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> {duplicateCount} duplicate{duplicateCount !== 1 ? "s" : ""}</span>}
              {invalidCount > 0 && <span className="flex items-center gap-1.5 text-sm text-red-700"><XCircle className="w-3.5 h-3.5" /> {invalidCount} invalid</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={reset} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">
                Change file
              </button>
              <button
                onClick={doImport}
                disabled={importing || validCount === 0}
                className="inline-flex items-center gap-2 bg-[#0F172A] text-white text-sm font-medium px-4 py-1.5 rounded-lg hover:bg-[#1e293b] disabled:opacity-50"
                data-testid="confirm-import"
              >
                {importing ? "Importing…" : `Import ${validCount} teacher${validCount !== 1 ? "s" : ""}`}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {validCount === 0 && (
            <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2 text-sm text-amber-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              No valid rows to import. Fix the errors below and re-upload.
            </div>
          )}

          {/* Preview rows */}
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="sticky top-0 bg-gray-50 border-b border-gray-100">
                <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <th className="text-left px-4 py-2.5 w-12">Row</th>
                  <th className="text-left px-4 py-2.5">Name</th>
                  <th className="text-left px-4 py-2.5">Email</th>
                  <th className="text-left px-4 py-2.5">Subject</th>
                  <th className="text-left px-4 py-2.5">Phone</th>
                  <th className="text-left px-4 py-2.5 w-32">Status</th>
                  <th className="text-left px-4 py-2.5">Issue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {preview.rows.map((r) => (
                  <tr key={r.row}
                    className={`transition-colors ${r.status === "valid" ? "hover:bg-gray-50/50" : r.status === "duplicate" ? "bg-amber-50/40 hover:bg-amber-50/60" : "bg-red-50/40 hover:bg-red-50/60"}`}
                    data-testid={`preview-row-${r.row}`}
                  >
                    <td className="px-4 py-2.5 text-xs text-gray-400 font-mono">{r.row}</td>
                    <td className="px-4 py-2.5 font-medium text-[#0F172A]">{r.name || <span className="text-red-400 italic">missing</span>}</td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">{r.email || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-600">{r.subject || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-600">{r.phone || "—"}</td>
                    <td className="px-4 py-2.5"><RowBadge status={r.status} /></td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 max-w-[200px]">
                      {r.errors?.length ? r.errors.join(", ") : r.status === "duplicate" ? (r.duplicate_in_db ? "Already in database" : "Duplicate in file") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Import result summary */}
      {result && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <h3 className="font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-500" /> Import Complete
          </h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-center">
              <div className="text-2xl font-bold text-emerald-700">{result.inserted}</div>
              <div className="text-xs text-emerald-600 mt-1">Imported</div>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-center">
              <div className="text-2xl font-bold text-amber-700">{result.skipped}</div>
              <div className="text-xs text-amber-600 mt-1">Skipped</div>
            </div>
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-gray-700">{result.total}</div>
              <div className="text-xs text-gray-500 mt-1">Total rows</div>
            </div>
          </div>

          {result.errors?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Skipped rows</p>
              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-3 py-2 text-gray-500 font-medium w-12">Row</th>
                      <th className="text-left px-3 py-2 text-gray-500 font-medium">Name</th>
                      <th className="text-left px-3 py-2 text-gray-500 font-medium">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {result.errors.map((e) => (
                      <tr key={e.row} className="bg-red-50/30">
                        <td className="px-3 py-2 text-gray-400 font-mono">{e.row}</td>
                        <td className="px-3 py-2 font-medium">{e.name || "—"}</td>
                        <td className="px-3 py-2 text-red-600">{e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <button onClick={reset} className="mt-4 text-sm text-[#4F46E5] hover:underline">
            Import another file
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const MODES = [
  { id: "individual", label: "Individual",       icon: User },
  { id: "bulk",       label: "Bulk Import",      icon: Users },
];

export default function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [schools,  setSchools]  = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [mode, setMode]         = useState("individual");
  const [loading, setLoading]   = useState(false);

  const load = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/teachers", { params: { school_id: schoolId } });
      setTeachers(data);
    } catch { toast.error("Could not load teachers."); }
    setLoading(false);
  }, [schoolId]);

  useEffect(() => {
    api.get("/schools").then((r) => {
      setSchools(r.data);
      if (r.data[0]) setSchoolId(r.data[0].id);
    });
  }, []);

  useEffect(() => { if (schoolId) load(); }, [schoolId, load]);

  const del = async (id, name) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    try {
      await api.delete(`/teachers/${id}`);
      toast.success(`${name} removed.`);
      load();
    } catch { toast.error("Could not remove teacher."); }
  };

  return (
    <div className="fade-up">
      <Header
        title="Teachers"
        subtitle={`${teachers.length} teacher${teachers.length !== 1 ? "s" : ""} registered${schools.find((s) => s.id === schoolId) ? ` — ${schools.find((s) => s.id === schoolId).name}` : ""}`}
      />

      {/* School selector + mode switcher */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <select
          value={schoolId}
          onChange={(e) => setSchoolId(e.target.value)}
          data-testid="teachers-school-filter"
          className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white min-w-[200px]"
        >
          {schools.length === 0 && <option value="">No schools found</option>}
          {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        {/* Mode switcher */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 ml-auto">
          {MODES.map((m) => (
            <button key={m.id} onClick={() => setMode(m.id)}
              className={`inline-flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-sm font-medium transition-all ${mode === m.id ? "bg-white shadow text-[#0F172A]" : "text-gray-500 hover:text-gray-700"}`}>
              <m.icon className="w-3.5 h-3.5" />
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Registration panel */}
      <div className="mt-4">
        {mode === "individual" ? (
          <IndividualForm schoolId={schoolId} onSaved={load} />
        ) : (
          <BulkForm schoolId={schoolId} onSaved={load} />
        )}
      </div>

      {/* Teachers table */}
      <div className="mt-6 rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Registered Teachers</span>
          <button onClick={load} className="w-7 h-7 grid place-items-center rounded-lg hover:bg-gray-100">
            <RefreshCw className="w-3.5 h-3.5 text-gray-400" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Subject</th>
                <th className="text-left px-5 py-3">Phone</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center py-10 text-gray-400 text-sm">Loading…</td></tr>
              ) : teachers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-gray-400 text-sm">
                    No teachers yet. Add one using the form above or import a CSV.
                  </td>
                </tr>
              ) : (
                teachers.map((t) => (
                  <tr key={t.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3 font-medium text-[#0F172A]">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-400 to-violet-400 grid place-items-center text-white text-xs font-bold shrink-0">
                          {(t.name || "?")[0].toUpperCase()}
                        </div>
                        {t.name}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-gray-500 text-xs">{t.email || "—"}</td>
                    <td className="px-5 py-3 text-gray-600">{t.subject || "—"}</td>
                    <td className="px-5 py-3 text-gray-600">{t.phone || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => del(t.id, t.name)}
                        className="w-7 h-7 grid place-items-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        data-testid={`delete-teacher-${t.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
